import { format } from 'date-fns';
import {
  AlertCircle,
  Ban,
  CheckCircle,
  ChevronDown,
  Clock,
  History,
  Loader2,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  ProofreadingJob,
  ProofreadingJobStatus,
} from '@/services/types/proofreading-types';

interface ProofreadingJobsDropdownProps {
  jobs: ProofreadingJob[];
  onSelectJob: (job: ProofreadingJob) => void;
  selectedJobId?: string;
  isLoading?: boolean;
}

const getStatusIcon = (status: ProofreadingJobStatus) => {
  switch (status) {
    case ProofreadingJobStatus.COMPLETED:
      return <CheckCircle className="h-3 w-3 text-green-600" />;
    case ProofreadingJobStatus.FAILED:
      return <XCircle className="h-3 w-3 text-red-600" />;
    case ProofreadingJobStatus.PROCESSING:
      return <Loader2 className="h-3 w-3 animate-spin text-blue-600" />;
    case ProofreadingJobStatus.PENDING:
      return <Clock className="h-3 w-3 text-amber-600" />;
    case ProofreadingJobStatus.CANCELLED:
      return <Ban className="h-3 w-3 text-gray-600" />;
    default:
      return <AlertCircle className="h-3 w-3 text-gray-600" />;
  }
};

const getStatusColor = (status: ProofreadingJobStatus) => {
  switch (status) {
    case ProofreadingJobStatus.COMPLETED:
      return 'bg-green-50 text-green-700 ring-green-600/20';
    case ProofreadingJobStatus.FAILED:
      return 'bg-red-50 text-red-700 ring-red-600/20';
    case ProofreadingJobStatus.PROCESSING:
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    case ProofreadingJobStatus.PENDING:
      return 'bg-amber-50 text-amber-700 ring-amber-600/20';
    case ProofreadingJobStatus.CANCELLED:
      return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    default:
      return 'bg-gray-50 text-gray-700 ring-gray-600/20';
  }
};

export const ProofreadingJobsDropdown: React.FC<
  ProofreadingJobsDropdownProps
> = ({ jobs, onSelectJob, selectedJobId, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (iso: string) => format(new Date(iso), 'MMM d • h:mm a');

  const handleSelectJob = (job: ProofreadingJob) => {
    onSelectJob(job);
    setIsOpen(false);
  };

  const selectedJob = selectedJobId
    ? jobs.find((j) => j.id === selectedJobId)
    : undefined;

  return (
    <div className="relative">
      <Button
        type="button"
        outline
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors h-[40px]"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <History className="h-4 w-4" />
        {selectedJob ? (
          <span className="truncate max-w-[16rem]">
            {formatDate(selectedJob.created_at)} • v{selectedJob.version} •{' '}
            {selectedJob.status.replace(/_/g, ' ')}
          </span>
        ) : (
          'History'
        )}
        {jobs.length > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
            {jobs.length}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsOpen(false);
              }
            }}
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 z-20 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-3 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-900">
                Recent Analysis Jobs
              </h3>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  <span className="ml-2 text-sm text-gray-500">Loading...</span>
                </div>
              ) : jobs.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="mx-auto h-8 w-8 text-gray-400 mb-2">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No analysis jobs yet</p>
                </div>
              ) : (
                <div className="py-1">
                  {jobs.map((job) => (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => handleSelectJob(job)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        job.id === selectedJobId
                          ? 'bg-blue-50 border-r-2 border-blue-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          {getStatusIcon(job.status)}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {formatDate(job.created_at)}
                            </p>
                            <p className="text-xs text-gray-500">
                              v{job.version} • {job.results.length} rules •{' '}
                              {job.status.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset capitalize ${getStatusColor(job.status)}`}
                        >
                          {job.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
